import { App, Button, Result, Spin } from '@yimoka/antd';
import { useLocation, useMiniNavigate } from '@yimoka/mini-router';
import { observer } from '@yimoka/react';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { DocumentTitle } from '@/components/document-title';
import { useTranslation } from '@/locales/i18n';
import { getUserToken } from '@/session/auth-session';

import { oidcAuthorizationBiz } from './biz/oidc-authorization';
import { useAccountStyles } from './index.style';

type OIDCPageStatus = 'error' | 'idle' | 'invalid' | 'loading' | 'success';

/** getSafeOIDCErrorText 限制授权服务器查询文本长度，React 仍会按纯文本转义展示。 */
function getSafeOIDCErrorText(value: string | null, fallback: string) {
  return (value?.trim() || fallback).slice(0, 500);
}

/** OIDCFlowPanel 为自动授权步骤提供一致的等待、参数错误、失败重试反馈。 */
function OIDCFlowPanel({
  status,
  loadingTitle,
  loadingDescription,
  onRetry,
}: {
  status: OIDCPageStatus;
  loadingTitle: string;
  loadingDescription: string;
  onRetry: () => void;
}) {
  const { styles } = useAccountStyles();
  const { t } = useTranslation();
  const invalid = status === 'invalid';
  const failed = status === 'error';
  let resultStatus: 'error' | 'info' | 'warning' = 'info';
  let resultTitle = loadingTitle;
  let resultDescription = loadingDescription;
  if (invalid) {
    resultStatus = 'error';
    resultTitle = t('Account:oidc.invalidChallenge');
    resultDescription = t('Account:oidc.invalidChallengeDesc');
  } else if (failed) {
    resultStatus = 'warning';
    resultTitle = t('Account:oidc.flowError');
    resultDescription = t('Account:oidc.flowErrorDesc');
  }

  return (
    <section className={styles.callbackBox} data-testid="oidc-flow-panel">
      <Result
        extra={failed ? (
          <Button type="primary" onClick={onRetry}>{t('Common:retry')}</Button>
        ) : undefined}
        icon={!invalid && !failed ? <Spin size="large" /> : undefined}
        status={resultStatus}
        subTitle={resultDescription}
        title={resultTitle}
      />
    </section>
  );
}

/** OIDCLoginPage 接受 Hydra login_challenge，并在平台登录缺失时保留原地址恢复。 */
export const OIDCLoginPage = observer(() => {
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const mountedPathRef = useRef('');
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (mountedPathRef.current === currentPath) return;
    mountedPathRef.current = currentPath;
    void oidcAuthorizationBiz.mountLogin(navigate, currentPath, location.search);
  }, [currentPath, location.search, navigate]);

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:oidc.loginTitle')} />
      <OIDCFlowPanel
        loadingDescription={t('Account:oidc.loginDescription')}
        loadingTitle={t('Account:oidc.loginTitle')}
        status={oidcAuthorizationBiz.loginStatus}
        onRetry={() => void oidcAuthorizationBiz.mountLogin(navigate, currentPath, location.search)}
      />
    </>
  );
});

/** OIDCConsentPage 自动接受 Hydra consent_challenge，保持旧 Web 的无交互授权行为。 */
export const OIDCConsentPage = observer(() => {
  const { t } = useTranslation();
  const navigate = useMiniNavigate();
  const location = useLocation();
  const mountedPathRef = useRef('');
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (mountedPathRef.current === currentPath) return;
    mountedPathRef.current = currentPath;
    void oidcAuthorizationBiz.mountConsent(navigate, currentPath, location.search);
  }, [currentPath, location.search, navigate]);

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:oidc.consentTitle')} />
      <OIDCFlowPanel
        loadingDescription={t('Account:oidc.consentDescription')}
        loadingTitle={t('Account:oidc.consentTitle')}
        status={oidcAuthorizationBiz.consentStatus}
        onRetry={() => void oidcAuthorizationBiz.mountConsent(navigate, currentPath, location.search)}
      />
    </>
  );
});

/** OIDCLogoutPage 接受 Hydra logout_challenge，并按后端返回地址完成整页跳转。 */
export const OIDCLogoutPage = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const mountedPathRef = useRef('');
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (mountedPathRef.current === currentPath) return;
    mountedPathRef.current = currentPath;
    void oidcAuthorizationBiz.mountLogout(location.search);
  }, [currentPath, location.search]);

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:oidc.logoutTitle')} />
      <OIDCFlowPanel
        loadingDescription={t('Account:oidc.logoutDescription')}
        loadingTitle={t('Account:oidc.logoutTitle')}
        status={oidcAuthorizationBiz.logoutStatus}
        onRetry={() => void oidcAuthorizationBiz.mountLogout(location.search)}
      />
    </>
  );
});

/** OIDCErrorPage 展示 Hydra 标准错误参数，并保持旧仓库对 access_denied 的 403 语义。 */
export function OIDCErrorPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const error = getSafeOIDCErrorText(params.get('error'), t('Account:oidc.authorizationFailed'));
  const description = getSafeOIDCErrorText(
    params.get('error_description') ?? params.get('message'),
    t('Account:oidc.authorizationFailedDesc'),
  );

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={error} />
      <Result
        data-testid="oidc-error-page"
        status={error === 'access_denied' ? '403' : '500'}
        subTitle={description}
        title={error}
      />
    </>
  );
}

/** OIDCLogoutSuccessfulPage 展示 Hydra 登出结果，并允许继续退出当前启云平台账号。 */
export const OIDCLogoutSuccessfulPage = observer(() => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useMiniNavigate();
  const hasUser = Boolean(getUserToken());
  const actions: ReactNode[] = [
    <Button
      key="home"
      size="large"
      type="primary"
      onClick={() => navigate(hasUser ? '/' : '/login')}
    >
      {t('Account:oidc.backHome')}
    </Button>,
  ];

  if (hasUser) {
    actions.push(
      <Button
        key="logout-account"
        loading={oidcAuthorizationBiz.accountLogoutStatus === 'loading'}
        size="large"
        onClick={() => {
          void oidcAuthorizationBiz.logoutPlatformAccount().then((success) => {
            if (!success) {
              void message.error(t('Account:oidc.platformLogoutError'));
              return;
            }
            void message.success(t('Account:oidc.platformLogoutSuccess'));
            navigate('/login');
          });
        }}
      >
        {t('Account:oidc.logoutPlatform')}
      </Button>,
    );
  }

  return (
    <>
      <DocumentTitle suffix={t('Account:brand.title')} title={t('Account:oidc.logoutSuccessTitle')} />
      <Result
        data-testid="oidc-logout-success-page"
        extra={actions}
        status="success"
        subTitle={t('Account:oidc.logoutSuccessDesc')}
        title={t('Account:oidc.logoutSuccessTitle')}
      />
    </>
  );
});
